import type { ReactElement } from 'react'
import { P, match } from 'ts-pattern'
import type { ReadonlyDeep } from 'type-fest'

import type { AnyStepContentElement } from '..'
import type { StepContentRendererProps } from './types'
import * as conditionUtils from './utils/conditions'
import * as elementUtils from './utils/elements'
import * as formUtils from './utils/forms'

const emptyFunction = () => undefined

const stepContentRenderer = <
  TStepContentRendererProps extends StepContentRendererProps<AnyStepContentElement>,
>(
  props: ReadonlyDeep<TStepContentRendererProps>
) => {
  const disabledConditions = conditionUtils.getDisabledConditionValuesFromElements(
    props.elements,
    props.flowValues
  )

  const elements = props.elements
    .filter((element) => {
      return match(element)
        .with(
          {
            condition: P.not(P.nullish),
          },
          (item) => {
            return conditionUtils.evaluateCondition(item.condition, props.flowValues)
          }
        )
        .otherwise(() => true)
    })
    .map((element) => elementUtils.disableElement(element, disabledConditions))

  const firstInputIndex = elements.findIndex((element) => element?.type === 'input')

  const shouldFormElementAutoSubmit = formUtils.shouldAutoSubmit(elements)

  // If props.onSubmit is not defined it will fall back to a fake function
  // Note.: props.onSubmit is the flow-controller (dispatch)
  const onSubmit = () => (typeof props.onSubmit === 'function' && props.onSubmit()) || emptyFunction

  // If props.onValueChange and element.onValueChange is not defined it will fall back to a fake function
  // Note.: props.onValueChange is the flow-controller, whereas element.onValueChange is the step config for the element
  const onValueChange = (element: any) =>
    (typeof props.onValueChange === 'function' && props.onValueChange(element)) || emptyFunction

  const onBlur = (element: any) =>
    (typeof props.onBlur === 'function' && props.onBlur(element)) || emptyFunction

  const mappedElements = elements.map((element: any, index) => {
    const isFormElement = formUtils.isFormFieldElement(element)
    // element.shouldAutoSubmit serves as an override to either manually disable or enable the props.onSubmit
    // The props.onSubmit is usually used to make Components auto submit the step.
    // By default, when shouldAutoSubmit is not provided, form elements will pass through the `shouldFormElementAutoSubmit`
    // And Custom Components will fall back to allow to be auto submitted if they are wired to props.onSubmit if no override
    // is given through the element.shouldAutoSubmit
    const shouldAutoSubmit =
      element.shouldAutoSubmit ?? (isFormElement ? shouldFormElementAutoSubmit : true)
    return {
      ...element,
      // If the element is autoSubmittable, which in some circumstances makes the whole step autoSubmittable
      shouldAutoSubmit,
      // This wires the onSubmit callback to props.onSubmit when autoSubmit is enabled otherwise
      // it will not execute anything since onSubmit should only be called if the step is auto submittable
      // It uses the -Handler naming because onSubmit is already defined on element
      submitHandler: () => shouldAutoSubmit && onSubmit(),
      // This wires the onValueChange callback if shouldAutoSubmit is not enabled.
      // The onValueChange controller calls then the element.onValueChange
      // It uses the -Handler naming because onValueChange is already defined on element
      valueChangeHandler: () => onValueChange(element),
      blurHandler: () => onBlur(element),
      // autoFocus is used by Form Elements, and it is passed as `true` if the current element is the first Input element
      // If there's no input element the firstInputIndex will be -1, which means that it will always be false
      autoFocus: element.autoFocus ?? index === firstInputIndex,

      // We are passing down both the StepContentRenderer component and the componentMap
      // instance, so that the elements themselves can generate step content when needed
      // This approach avoids generating circular import dependencies
      stepContentRenderer,
      componentMap: props.componentMap,
      flowName: props.flowName,
    } as const satisfies AnyStepContentElement
  })

  // This maps the stepConfig into an array of React elements
  // Note: It is recommended to surround this with UiKit.Stack in the Consumer side.
  return mappedElements.map<ReactElement>(elementUtils.mapElement(props.componentMap))
}

export default stepContentRenderer
